import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
    Building2,
    Globe,
    Briefcase,
    Users,
    User,
    Upload,
    Image,
    Trash2,
    ChevronRight,
    ChevronLeft,
    CheckCircle2,
    Loader2,
    Sparkles,
    ClipboardList,
    GraduationCap,
    FileText,
    MessageSquare,
    ArrowRight,
    UserPlus,
    Calendar,
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const INDUSTRIES = [
    'Technology', 'Healthcare', 'Finance', 'Manufacturing',
    'Retail', 'Education', 'Construction', 'Hospitality',
    'Legal', 'Non-Profit', 'Other',
];

const COMPANY_SIZES = [
    '1-10', '11-50', '50-200', '200-500', '500+',
];

const TOTAL_STEPS = 5;

// ─── Progress Bar ──────────────────────────────────
const ProgressBar = ({ currentStep }) => {
    const stepLabels = ['Company', 'Profile', 'Brand', 'Team', 'Done'];
    return (
        <div className="w-full max-w-lg mx-auto mb-10">
            <div className="flex items-center justify-between mb-2">
                {stepLabels.map((label, idx) => (
                    <div key={idx} className="flex flex-col items-center">
                        <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${idx < currentStep
                                ? 'bg-accent text-white shadow-lg shadow-accent/30'
                                : idx === currentStep
                                    ? 'bg-accent text-white shadow-lg shadow-accent/40 ring-4 ring-accent/20'
                                    : 'bg-secondary text-muted-foreground'
                                }`}
                        >
                            {idx < currentStep ? <CheckCircle2 size={18} /> : idx + 1}
                        </div>
                        <span className={`text-xs mt-1.5 font-medium ${idx <= currentStep ? 'text-accent' : 'text-muted-foreground'
                            }`}>
                            {label}
                        </span>
                    </div>
                ))}
            </div>
            <div className="relative h-1.5 bg-secondary rounded-full mt-1 overflow-hidden">
                <div
                    className="absolute h-full bg-gradient-to-r from-accent to-accent/80 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${(currentStep / (TOTAL_STEPS - 1)) * 100}%` }}
                />
            </div>
        </div>
    );
};

// ─── Input Field Component ─────────────────────────
const InputField = ({ icon: Icon, label, required, ...props }) => (
    <div>
        <label className="block text-sm font-medium text-foreground mb-2">
            {label} {required && <span className="text-destructive">*</span>}
        </label>
        <div className="relative">
            {Icon && <Icon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
            <input
                className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground transition-all`}
                {...props}
            />
        </div>
    </div>
);

// ─── Select Field Component ────────────────────────
const SelectField = ({ icon: Icon, label, options, placeholder, ...props }) => (
    <div>
        <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
        <div className="relative">
            {Icon && <Icon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
            <select
                className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground appearance-none transition-all`}
                {...props}
            >
                <option value="">{placeholder}</option>
                {options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                ))}
            </select>
        </div>
    </div>
);

const SetupWizard = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const { theme } = useTheme();
    const fileInputRef = useRef(null);

    const [currentStep, setCurrentStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [animating, setAnimating] = useState(false);

    // Step 1: Company Profile
    const [companyData, setCompanyData] = useState({
        company_name: user?.company_name || '',
        company_website: '',
        industry: '',
        company_size: '',
    });

    // Step 2: User Profile
    const [profileData, setProfileData] = useState({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        position: user?.position || '',
    });

    // Step 3: Branding
    const [logoUrl, setLogoUrl] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);
    const [skipBranding, setSkipBranding] = useState(false);

    // Step 4: First Employee
    const [addEmployee, setAddEmployee] = useState(false);
    const [employeeData, setEmployeeData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        lifecycle_stage: 'preboarding',
        start_date: '',
    });
    const [employeeAdded, setEmployeeAdded] = useState(false);

    // Resume from saved step
    useEffect(() => {
        const savedStep = user?.setup_step || 0;
        if (savedStep > 0 && savedStep < TOTAL_STEPS) {
            setCurrentStep(savedStep);
        }
    }, []);

    // Step validation
    const isStepValid = () => {
        switch (currentStep) {
            case 0: // Welcome & Company Profile
                return companyData.company_name.trim().length > 0;
            case 1: // Your Profile
                return profileData.first_name.trim().length > 0 && profileData.last_name.trim().length > 0;
            case 2: // Branding (always valid — optional)
                return true;
            case 3: // First Employee (valid if skipping or if form is filled)
                if (!addEmployee) return true;
                return (
                    employeeData.first_name.trim().length > 0 &&
                    employeeData.last_name.trim().length > 0 &&
                    employeeData.email.trim().length > 0 &&
                    (employeeData.lifecycle_stage === 'active' || employeeData.start_date)
                );
            default:
                return true;
        }
    };

    // Save progress for current step
    const saveStepProgress = async (step, data = null) => {
        try {
            await api.put('/setup/progress', { step, data });
        } catch (err) {
            console.error('Failed to save progress:', err);
        }
    };

    // Navigate between steps
    const goToStep = async (direction) => {
        if (animating) return;
        setAnimating(true);
        setSaving(true);

        const nextStep = currentStep + direction;

        try {
            if (direction > 0) {
                // Save data on forward navigation
                switch (currentStep) {
                    case 0:
                        await saveStepProgress(1, {
                            company_name: companyData.company_name,
                            company_website: companyData.company_website,
                            industry: companyData.industry,
                            company_size: companyData.company_size,
                        });
                        break;
                    case 1:
                        await saveStepProgress(2, {
                            first_name: profileData.first_name,
                            last_name: profileData.last_name,
                            position: profileData.position,
                        });
                        break;
                    case 2:
                        await saveStepProgress(3);
                        break;
                    case 3:
                        // Add employee if toggled
                        if (addEmployee && !employeeAdded) {
                            try {
                                await api.post('/employees', {
                                    first_name: employeeData.first_name,
                                    last_name: employeeData.last_name,
                                    email: employeeData.email,
                                    lifecycle_stage: employeeData.lifecycle_stage,
                                    start_date: employeeData.lifecycle_stage !== 'active' ? employeeData.start_date : undefined,
                                });
                                setEmployeeAdded(true);
                                toast.success(`${employeeData.first_name} has been added!`);
                            } catch (err) {
                                toast.error(err.response?.data?.error || 'Failed to add employee');
                                setSaving(false);
                                setAnimating(false);
                                return;
                            }
                        }
                        await saveStepProgress(4);
                        break;
                }
            }
        } catch (err) {
            console.error('Step transition error:', err);
        }

        setTimeout(() => {
            setCurrentStep(nextStep);
            setSaving(false);
            setAnimating(false);
        }, 200);
    };

    // Complete the wizard
    const handleComplete = async () => {
        setSaving(true);
        try {
            await api.put('/setup/complete');
            // Update user context so ProtectedRoute won't redirect back
            updateUser({ ...user, setup_completed: true, setup_step: 5 });
            toast.success('Setup complete! Welcome to envion.');
            navigate('/admin', { replace: true });
        } catch (err) {
            toast.error('Failed to complete setup');
        } finally {
            setSaving(false);
        }
    };

    // Logo upload handler
    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please select a JPG, PNG, WebP, or SVG image');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be less than 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = (ev) => setLogoPreview(ev.target.result);
        reader.readAsDataURL(file);

        setLogoUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post('/company/logo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setLogoUrl(response.data.logo_url);
            setLogoPreview(null);
            toast.success('Logo uploaded!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to upload logo');
            setLogoPreview(null);
        } finally {
            setLogoUploading(false);
        }
    };

    const handleRemoveLogo = async () => {
        try {
            await api.delete('/company/logo');
            setLogoUrl(null);
            setLogoPreview(null);
            toast.success('Logo removed');
        } catch (err) {
            toast.error('Failed to remove logo');
        }
    };

    // ─── Step Content ──────────────────────────────────

    // Step 1: Welcome & Company Profile
    const renderStep1 = () => (
        <div className="animate-fadeIn">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Sparkles size={32} className="text-accent" />
                </div>
                <h2 className="text-3xl font-bold text-foreground mb-2">Welcome to envion!</h2>
                <p className="text-muted-foreground text-lg">Let's set up your company account. This will only take a few minutes.</p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Company Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <InputField
                        icon={Building2}
                        label="Company Name"
                        required
                        type="text"
                        value={companyData.company_name}
                        onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })}
                        placeholder="Your Company Name"
                    />
                    <InputField
                        icon={Globe}
                        label="Website"
                        type="url"
                        value={companyData.company_website}
                        onChange={(e) => setCompanyData({ ...companyData, company_website: e.target.value })}
                        placeholder="https://yourcompany.com"
                    />
                    <SelectField
                        icon={Briefcase}
                        label="Industry"
                        options={INDUSTRIES}
                        placeholder="Select industry"
                        value={companyData.industry}
                        onChange={(e) => setCompanyData({ ...companyData, industry: e.target.value })}
                    />
                    <SelectField
                        icon={Users}
                        label="Company Size"
                        options={COMPANY_SIZES.map(s => `${s} employees`)}
                        placeholder="Select size"
                        value={companyData.company_size}
                        onChange={(e) => setCompanyData({ ...companyData, company_size: e.target.value })}
                    />
                </div>
            </div>
        </div>
    );

    // Step 2: Your Profile
    const renderStep2 = () => (
        <div className="animate-fadeIn">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <User size={32} className="text-accent" />
                </div>
                <h2 className="text-3xl font-bold text-foreground mb-2">Tell us about yourself</h2>
                <p className="text-muted-foreground text-lg">Set up your personal profile in envion.</p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Your Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <InputField
                        label="First Name"
                        required
                        type="text"
                        value={profileData.first_name}
                        onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                        placeholder="John"
                    />
                    <InputField
                        label="Last Name"
                        required
                        type="text"
                        value={profileData.last_name}
                        onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                        placeholder="Smith"
                    />
                    <InputField
                        icon={Briefcase}
                        label="Job Title"
                        type="text"
                        value={profileData.position}
                        onChange={(e) => setProfileData({ ...profileData, position: e.target.value })}
                        placeholder="CEO"
                    />
                </div>

                {/* Role Badge */}
                <div className="flex items-center gap-3 p-4 bg-accent/5 border border-accent/20 rounded-xl mt-4">
                    <div className="w-10 h-10 bg-accent/10 text-accent rounded-lg flex items-center justify-center">
                        <CheckCircle2 size={20} />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-foreground">Your Role: Owner</p>
                        <p className="text-xs text-muted-foreground">You'll have full access to manage everything in envion.</p>
                    </div>
                </div>
            </div>
        </div>
    );

    // Step 3: Company Branding (Optional)
    const renderStep3 = () => (
        <div className="animate-fadeIn">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Image size={32} className="text-accent" />
                </div>
                <h2 className="text-3xl font-bold text-foreground mb-2">Add your company logo</h2>
                <p className="text-muted-foreground text-lg">
                    Customize how envion looks for your team. You can always do this later in Settings.
                </p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-6">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-5">Company Logo</h3>

                <div className="flex flex-col items-center gap-6">
                    {/* Upload Area */}
                    <div
                        className="relative group w-40 h-40 rounded-2xl border-2 border-dashed border-border bg-secondary/50 
                        flex flex-col items-center justify-center cursor-pointer hover:border-accent/50 hover:bg-accent/5 transition-all overflow-hidden"
                        onClick={() => !logoUploading && fileInputRef.current?.click()}
                    >
                        {logoPreview || logoUrl ? (
                            <img
                                src={logoPreview || logoUrl}
                                alt="Company logo"
                                className="w-full h-full object-cover rounded-2xl"
                            />
                        ) : (
                            <>
                                <Upload size={28} className="text-muted-foreground mb-2" />
                                <span className="text-xs text-muted-foreground text-center px-2">
                                    Click to upload
                                </span>
                            </>
                        )}
                        {logoUploading && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-2xl">
                                <Loader2 size={28} className="text-white animate-spin" />
                            </div>
                        )}
                        {logoUrl && !logoUploading && (
                            <button
                                onClick={(e) => { e.stopPropagation(); handleRemoveLogo(); }}
                                className="absolute -top-2 -right-2 w-7 h-7 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                    />
                    <p className="text-xs text-muted-foreground text-center">
                        PNG, JPG, SVG or WebP. Max 5MB. Recommended: 200×200px
                    </p>
                </div>

                {/* Skip checkbox */}
                <label className="flex items-center gap-2 mt-6 cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors">
                    <input
                        type="checkbox"
                        checked={skipBranding}
                        onChange={(e) => setSkipBranding(e.target.checked)}
                        className="w-4 h-4 rounded border-border text-accent focus:ring-accent/50"
                    />
                    Skip for now
                </label>
            </div>
        </div>
    );

    // Step 4: First Employee (Optional)
    const renderStep4 = () => (
        <div className="animate-fadeIn">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <UserPlus size={32} className="text-accent" />
                </div>
                <h2 className="text-3xl font-bold text-foreground mb-2">Add your first employee</h2>
                <p className="text-muted-foreground text-lg">
                    Want to add a team member right away? You can always add employees later.
                </p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-6">
                {employeeAdded ? (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 size={32} className="text-green-500" />
                        </div>
                        <p className="text-lg font-semibold text-foreground mb-1">
                            {employeeData.first_name} has been added!
                        </p>
                        <p className="text-sm text-muted-foreground">
                            They'll receive a welcome email to set up their account.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Toggle */}
                        <label className="flex items-center gap-3 p-4 bg-secondary rounded-xl cursor-pointer mb-6 group">
                            <input
                                type="checkbox"
                                checked={addEmployee}
                                onChange={(e) => setAddEmployee(e.target.checked)}
                                className="w-5 h-5 rounded border-border text-accent focus:ring-accent/50"
                            />
                            <div>
                                <p className="font-medium text-foreground group-hover:text-accent transition-colors">
                                    Add first employee now
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Invite a team member to start using envion
                                </p>
                            </div>
                        </label>

                        {addEmployee && (
                            <div className="space-y-5 animate-fadeIn">
                                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                                    Employee Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <InputField
                                        label="First Name"
                                        required
                                        type="text"
                                        value={employeeData.first_name}
                                        onChange={(e) => setEmployeeData({ ...employeeData, first_name: e.target.value })}
                                        placeholder="Jane"
                                    />
                                    <InputField
                                        label="Last Name"
                                        required
                                        type="text"
                                        value={employeeData.last_name}
                                        onChange={(e) => setEmployeeData({ ...employeeData, last_name: e.target.value })}
                                        placeholder="Doe"
                                    />
                                    <InputField
                                        label="Email"
                                        required
                                        type="email"
                                        value={employeeData.email}
                                        onChange={(e) => setEmployeeData({ ...employeeData, email: e.target.value })}
                                        placeholder="jane@company.com"
                                    />
                                </div>

                                {/* Lifecycle Stage */}
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-3">Lifecycle Stage</label>
                                    <div className="space-y-2">
                                        {[
                                            { value: 'preboarding', label: 'Preboarding', desc: "Hasn't started yet" },
                                            { value: 'onboarding', label: 'Onboarding', desc: 'Starting soon or just started' },
                                            { value: 'active', label: 'Active', desc: 'Already working here' },
                                        ].map((stage) => (
                                            <label
                                                key={stage.value}
                                                className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${employeeData.lifecycle_stage === stage.value
                                                    ? 'border-accent bg-accent/5'
                                                    : 'border-border hover:border-accent/30'
                                                    }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="lifecycle_stage"
                                                    value={stage.value}
                                                    checked={employeeData.lifecycle_stage === stage.value}
                                                    onChange={(e) => setEmployeeData({ ...employeeData, lifecycle_stage: e.target.value })}
                                                    className="text-accent focus:ring-accent/50"
                                                />
                                                <div>
                                                    <p className="text-sm font-medium text-foreground">{stage.label}</p>
                                                    <p className="text-xs text-muted-foreground">{stage.desc}</p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Start Date (if preboarding/onboarding) */}
                                {employeeData.lifecycle_stage !== 'active' && (
                                    <InputField
                                        icon={Calendar}
                                        label="Start Date"
                                        required
                                        type="date"
                                        value={employeeData.start_date}
                                        onChange={(e) => setEmployeeData({ ...employeeData, start_date: e.target.value })}
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );

    // Step 5: Complete
    const renderStep5 = () => {
        const quickActions = [
            { icon: ClipboardList, label: 'Create Your First Task', desc: 'Assign tasks to your team members', path: '/admin/activity-centre/tasks', color: 'text-blue-500 bg-blue-500/10' },
            { icon: GraduationCap, label: 'Build an Onboarding Course', desc: 'Create a structured onboarding journey', path: '/admin/activity-centre/courses', color: 'text-purple-500 bg-purple-500/10' },
            { icon: Users, label: 'Add Employees', desc: 'Invite your team to envion', path: '/admin/employees', color: 'text-green-500 bg-green-500/10' },
            { icon: FileText, label: 'Upload Documents', desc: 'Share company handbooks and policies', path: '/admin/files', color: 'text-orange-500 bg-orange-500/10' },
            { icon: MessageSquare, label: 'Chat with AI Assistant', desc: 'Get help setting up your onboarding process', path: '/admin/ai', color: 'text-pink-500 bg-pink-500/10' },
        ];

        return (
            <div className="animate-fadeIn">
                <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                        <CheckCircle2 size={40} className="text-green-500" />
                    </div>
                    <h2 className="text-3xl font-bold text-foreground mb-2">You're all set!</h2>
                    <p className="text-muted-foreground text-lg">
                        Your envion account is ready. Here's what you can do next:
                    </p>
                </div>

                <div className="bg-card border border-border rounded-2xl p-6">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Quick Actions</h3>
                    <div className="space-y-3">
                        {quickActions.map((action, idx) => (
                            <button
                                key={idx}
                                onClick={() => {
                                    handleComplete().then(() => {
                                        navigate(action.path, { replace: true });
                                    });
                                }}
                                className="w-full flex items-center gap-4 p-4 rounded-xl border border-border hover:border-accent/30 hover:bg-secondary/50 transition-all text-left group"
                            >
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${action.color}`}>
                                    <action.icon size={20} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-foreground group-hover:text-accent transition-colors">{action.label}</p>
                                    <p className="text-xs text-muted-foreground">{action.desc}</p>
                                </div>
                                <ArrowRight size={16} className="text-muted-foreground group-hover:text-accent transition-colors" />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // ─── Render ────────────────────────────────────────
    const stepRenderers = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Top Bar */}
            <div className="border-b border-border bg-card/50 backdrop-blur-sm">
                <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-md">
                            e
                        </div>
                        <span className="font-semibold text-foreground text-lg">envion</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                        Step {currentStep + 1} of {TOTAL_STEPS}
                    </span>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
                <div className="w-full max-w-2xl">
                    <ProgressBar currentStep={currentStep} />
                    {stepRenderers[currentStep]()}

                    {/* Navigation Buttons */}
                    <div className="flex items-center justify-between mt-8">
                        {currentStep > 0 && currentStep < TOTAL_STEPS - 1 ? (
                            <button
                                onClick={() => goToStep(-1)}
                                disabled={saving}
                                className="flex items-center gap-2 px-5 py-3 text-muted-foreground hover:text-foreground transition-colors rounded-xl disabled:opacity-50"
                            >
                                <ChevronLeft size={18} />
                                Back
                            </button>
                        ) : (
                            <div />
                        )}

                        {currentStep < TOTAL_STEPS - 1 ? (
                            <button
                                onClick={() => goToStep(1)}
                                disabled={!isStepValid() || saving}
                                className="flex items-center gap-2 px-8 py-3 bg-accent text-white rounded-xl font-semibold shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? (
                                    <Loader2 size={18} className="animate-spin" />
                                ) : (
                                    <>
                                        Continue
                                        <ChevronRight size={18} />
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={handleComplete}
                                disabled={saving}
                                className="flex items-center gap-2 px-8 py-3 bg-accent text-white rounded-xl font-semibold shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all disabled:opacity-50"
                            >
                                {saving ? (
                                    <Loader2 size={18} className="animate-spin" />
                                ) : (
                                    <>
                                        Go to Dashboard
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="py-4 text-center text-xs text-muted-foreground border-t border-border">
                Powered by <span className="font-semibold text-accent">envion</span>
            </div>
        </div>
    );
};

export default SetupWizard;
